import {
	LoadingOutlined,
	MinusOutlined,
	PictureOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { InfoCircle } from '@carespace-icons/general/infoCircle';
import { highlightedBoneColor } from '@organisms/OVideoPoseEstimatorUpdated/mediapipe';
import { ICustomRomSession, OmniRomRomPatientResult } from '@stores/interfaces';
import {
	Collapse,
	Flex,
	Image,
	Popover,
	Spin,
	Table,
	Tag,
	Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { evalScore } from './jointsTemplateData';

const { Text } = Typography;

interface ContentRomResultProps {
	getColorForCategory: (score: number) => string;
	getCategoryName: (score: number) => string;
	selectedRom: ICustomRomSession;
	isPdf?: boolean;
}

const calculateRanking = (score: number) => {
	if (score >= 91) return 10;
	if (score >= 81) return 9;
	if (score >= 71) return 8;
	if (score >= 61) return 7;
	if (score >= 51) return 6;
	if (score >= 41) return 5;
	if (score >= 31) return 4;
	if (score >= 21) return 3;
	if (score >= 11) return 2;
	if (score >= 1) return 1;
	return 0;
};

const hexToRgb = (hex: string): [number, number, number] => {
	const r: number = parseInt(hex.slice(1, 3), 16);
	const g: number = parseInt(hex.slice(3, 5), 16);
	const b: number = parseInt(hex.slice(5, 7), 16);
	return [r, g, b];
};

const isPurplePixel = (
	r: number,
	g: number,
	b: number,
	targetR: number,
	targetG: number,
	targetB: number,
): boolean => {
	const tolerance = 30;

	const rDiff = Math.abs(r - targetR);
	const gDiff = Math.abs(g - targetG);
	const bDiff = Math.abs(b - targetB);

	return rDiff < tolerance && gDiff < tolerance && bDiff < tolerance;
};

const ContentRomResult = ({
	getColorForCategory,
	getCategoryName,
	selectedRom,
	isPdf = false,
}: ContentRomResultProps) => {
	const { t } = useTranslation();
	const dataSource = selectedRom?.romPatientResults;
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const [processedImage, setProcessedImage] = useState<string | null>(null);
	const [isProcessingImage, setIsProcessingImage] = useState(false);
	const [selectedRecord, setSelectedRecord] =
		useState<OmniRomRomPatientResult | null>(null);
	const [loadingIconId, setLoadingIconId] = useState<string | null>(null);

	const showModal = async (
		src: string,
		record: OmniRomRomPatientResult,
		iconId?: string,
	) => {
		if (iconId) {
			setLoadingIconId(iconId);
		}
		setSelectedRecord(record);
		setIsProcessingImage(true);

		try {
			const score = evalScore(record);

			const skeletonColor = getColorForCategory(score);

			const processedSrc = await new Promise((resolve, reject) => {
				const img = document.createElement('img');
				img.crossOrigin = 'Anonymous';

				img.onload = () => {
					try {
						const canvas = document.createElement('canvas');
						canvas.width = img.width;
						canvas.height = img.height;
						const ctx = canvas.getContext('2d');

						if (!ctx) {
							console.error('Failed to get canvas context');
							reject('Failed to get canvas context');
							return;
						}

						ctx.drawImage(img, 0, 0);

						const imageData = ctx.getImageData(
							0,
							0,
							canvas.width,
							canvas.height,
						);
						const data = imageData.data;

						const [targetR, targetG, targetB] = hexToRgb(skeletonColor);
						const [purpleR, purpleG, purpleB] = hexToRgb(highlightedBoneColor);

						for (let i = 0; i < data.length; i += 4) {
							const r = data[i];
							const g = data[i + 1];
							const b = data[i + 2];

							if (isPurplePixel(r, g, b, purpleR, purpleG, purpleB)) {
								data[i] = targetR;
								data[i + 1] = targetG;
								data[i + 2] = targetB;
							}
						}

						ctx.putImageData(imageData, 0, 0);
						resolve(canvas.toDataURL('image/png'));
					} catch (err) {
						console.error('Canvas processing error:', err);
						reject(err);
					}
				};

				img.onerror = () => {
					console.error('Failed to load image');
					reject('Failed to load image');
				};

				img.src = src;
			});

			setProcessedImage(processedSrc as string);
			setSelectedImage(src);
			setModalOpen(true);
		} catch (err) {
			console.error('Image processing error:', err);
			setSelectedImage(src);
			setModalOpen(true);
		} finally {
			setIsProcessingImage(false);
			setLoadingIconId(null);
		}
	};

	const processImage = (src: string, record: OmniRomRomPatientResult) => {
		if (!src || isProcessingImage) return;

		setIsProcessingImage(true);

		const score = evalScore(record);

		const skeletonColor = getColorForCategory(score);

		setTimeout(() => {
			try {
				const img = document.createElement('img');
				img.crossOrigin = 'Anonymous';

				img.onload = () => {
					try {
						const canvas = document.createElement('canvas');
						canvas.width = img.width;
						canvas.height = img.height;
						const ctx = canvas.getContext('2d');

						if (!ctx) {
							console.error('Failed to get canvas context');
							setIsProcessingImage(false);
							return;
						}

						ctx.drawImage(img, 0, 0);

						const imageData = ctx.getImageData(
							0,
							0,
							canvas.width,
							canvas.height,
						);
						const data = imageData.data;

						const [targetR, targetG, targetB] = hexToRgb(skeletonColor);
						const [purpleR, purpleG, purpleB] = hexToRgb(highlightedBoneColor);

						for (let i = 0; i < data.length; i += 4) {
							const r = data[i];
							const g = data[i + 1];
							const b = data[i + 2];

							if (isPurplePixel(r, g, b, purpleR, purpleG, purpleB)) {
								data[i] = targetR;
								data[i + 1] = targetG;
								data[i + 2] = targetB;
							}
						}

						ctx.putImageData(imageData, 0, 0);
						setProcessedImage(canvas.toDataURL('image/png'));
					} catch (err) {
						console.error('Canvas processing error:', err);
					} finally {
						setIsProcessingImage(false);
					}
				};

				img.onerror = () => {
					console.error('Failed to load image');
					setIsProcessingImage(false);
				};

				img.src = src;
			} catch (err) {
				console.error('Image processing error:', err);
				setIsProcessingImage(false);
			}
		}, 0);
	};

	useEffect(() => {
		if (selectedImage && selectedRecord && modalOpen) {
			processImage(selectedImage, selectedRecord);
		}
	}, [selectedRecord]);

	const categories = [
		{
			name: t('Patient.data.omnirom.optimalMobility'),
			range: '90 - 100',
			color: '#52C41A',
			scoreRange: [90, 100],
			description: t('Patient.data.omnirom.optimalMobilityDesc'),
		},
		{
			name: t('Patient.data.omnirom.functionalMobility'),
			range: '75 - 89',
			color: '#73D13D',
			scoreRange: [75, 89],
			description: t('Patient.data.omnirom.functionalMobilityDesc'),
		},
		{
			name: t('Patient.data.omnirom.moderateRestriction'),
			range: '60 - 74',
			color: '#FAAD14',
			scoreRange: [60, 74],
			description: t('Patient.data.omnirom.moderateRestrictionDesc'),
		},
		{
			name: t('Patient.data.omnirom.restrictedMobility'),
			range: '40 - 59',
			color: '#F59712',
			scoreRange: [40, 59],
			description: t('Patient.data.omnirom.limitedMobilityDesc'),
		},
		{
			name: t('Patient.data.omnirom.severelyRestricted'),
			range: '20 - 39',
			color: '#F2615B',
			scoreRange: [20, 39],
			description: t('Patient.data.omnirom.restrictedMobilityDesc'),
		},
		{
			name: t('Patient.data.omnirom.criticallyImpaired'),
			range: 'Below 20',
			color: '#D9363E',
			scoreRange: [0, 19],
			description: t('Patient.data.omnirom.criticallyImpairedDesc'),
		},
	];

	const flattenDataSource = dataSource
		?.map(item => item?.results?.map(result => ({ ...item, ...result })))
		?.flat();

	const categorizedData = flattenDataSource?.map(record => {
		const rawScore = evalScore(record);
		const score = rawScore !== undefined
			? (rawScore >= 100 ? 100 : rawScore)
			: evalScore(record);
		const name = getCategoryName(score);
		return {
			...record,
			category: name ? name : 'Uncategorized',
			score,
		};
	});

	const groupedData = categories?.map(category => {
		const filteredData = categorizedData?.filter(
			record => record?.category === category?.name,
		);
		return {
			categoryName: category?.name,
			categoryColor: category?.color,
			data: filteredData,
			range: category?.range,
			categoryDescription: category?.description,
		};
	});

	const columns = [
		{
			title: '',
			dataIndex: 'screenshot',
			key: 'screenshot',
			width: '40px',
			render: (src: string, record: OmniRomRomPatientResult) => {
				const iconId = `icon-${record?.id || Math.random().toString(36).substring(7)}`;

				return (
					<div
						className="pdf-link"
						data-href={new URL(src, window.location.origin).href}>
						{isProcessingImage && loadingIconId === iconId ? (
							<Spin
								indicator={
									<LoadingOutlined
										style={{ fontSize: 24, color: '#1890ff' }}
										spin
									/>
								}
							/>
						) : (
							<PictureOutlined
								style={{ fontSize: 24, cursor: 'pointer', color: '#999' }}
								onClick={() => showModal(src, record, iconId)}
							/>
						)}
					</div>
				);
			},
		},
		{
			title: (
				<p className={isPdf ? 'pdf-margin' : ''}>
					{t('Patient.data.omnirom.measure')}
				</p>
			),
			dataIndex: 'title',
			key: 'title',
			width: '100%',
			onCell: () => ({ style: { flex: 1 } }),
			render: (_: string, record: OmniRomRomPatientResult) => (
				<Text>
					<p className={isPdf ? 'pdf-margin' : ''}>
						{record?.romProgramExercise?.strapiOmniRomExercise?.name ||
							record?.title}
					</p>
				</Text>
			),
		},
		{
			title: (
				<Popover content={t('Patient.data.omnirom.normalInfo')} title="Normal">
					<Flex align="space-between" gap="small">
						<span className={isPdf ? 'pdf-margin' : ''}>
							{t('Patient.data.omnirom.normal')}
						</span>
						<span className="cursor-pointer">
							<InfoCircle width={16} height={16} color="stroke-gray-600" />
						</span>
					</Flex>
				</Popover>
			),
			dataIndex: 'normal',
			key: 'normal',
			align: 'center',
			render: (_: string, record: OmniRomRomPatientResult) => {
				const normalValue = record?.normal || 0;
				return (
					<Text type="secondary" className={isPdf ? 'pdf-margin' : ''}>
						{normalValue}
					</Text>
				);
			},
		},
		{
			title: (
				<Popover content={t('Patient.data.omnirom.wflInfo')} title="WFL">
					<Flex align="space-between" gap="small">
						<span className={isPdf ? 'pdf-margin' : ''}>
							{t('Patient.data.omnirom.wfl')}
						</span>
						<span className="cursor-pointer">
							<InfoCircle width={16} height={16} color="stroke-gray-600" />
						</span>
					</Flex>
				</Popover>
			),
			dataIndex: 'wfl',
			key: 'wfl',
			align: 'center',
			render: (_: string, record: OmniRomRomPatientResult) => {
				const wflValue = record?.wfl || 0;
				return (
					<Text className={isPdf ? 'pdf-margin' : ''} type="secondary">
						{wflValue}
					</Text>
				);
			},
		},
		{
			title: (
				<p className={isPdf ? 'pdf-margin' : ''}>
					{t('Patient.data.omnirom.value')}
				</p>
			),
			dataIndex: 'value',
			key: 'value',
			align: 'center',
			render: (_: string, record: OmniRomRomPatientResult) => {
				const score = evalScore(record);
				const color = getColorForCategory(score);
				return (
					<span style={{ color: color }} className={isPdf ? 'pdf-margin' : ''}>
						{record?.value}
					</span>
				);
			},
		},
		{
			title: (
				<p className={isPdf ? 'pdf-margin' : ''}>
					{t('Patient.data.omnirom.ranking')}
				</p>
			),
			dataIndex: 'ranking',
			key: 'ranking',
			align: 'center',
			render: (_: string, record: OmniRomRomPatientResult) => {
				const score = evalScore(record);
				const color = getColorForCategory(score);
				return (
					<span
						style={{ color: color, fontWeight: 'bold' }}
						className={isPdf ? 'pdf-margin' : ''}>
						{calculateRanking(score)}
					</span>
				);
			},
		},
		{
			title: (
				<p className={isPdf ? 'pdf-margin' : ''}>
					{t('Patient.data.omnirom.score')}
				</p>
			),
			dataIndex: 'score',
			key: 'score',
			align: 'center',
			render: (_: string, record: OmniRomRomPatientResult) => {
				const score = evalScore(record);
				const color = getColorForCategory(score || 0);
				return (
					<Tag
						style={{ marginInlineEnd: 0, width: '100%', textAlign: 'center' }}
						color={color || 'default'}>
						<p className={isPdf ? 'pdf-margin' : ''}>{Math.round(score || 0)}</p>
					</Tag>
				);
			},
		},
	];

	const defaultActiveKeys = groupedData
		?.map((category, index) =>
			category?.data && category?.data.length > 0 ? index.toString() : null,
		)
		?.filter(index => index !== null);

	const [activeKeys, setActiveKeys] = useState<string[]>(
		(defaultActiveKeys as string[]) || [],
	);

	useEffect(() => {
		setActiveKeys((defaultActiveKeys as string[]) || []);
	}, [isPdf, groupedData?.length]);

	return (
		<div>
			<Collapse
				accordion={false}
				bordered={false}
				expandIcon={({ isActive }) =>
					isActive ? <MinusOutlined /> : <PlusOutlined />
				}
				className={`collapse-categories-results`}
				style={{ background: 'transparent' }}
				activeKey={activeKeys}
				onChange={keys => setActiveKeys(Array.isArray(keys) ? keys : [keys])}>
				{groupedData?.map((category, index) => (
					<Collapse.Panel
						key={index?.toString()}
						data-color={category?.categoryName}
						className={`header-panel !bg-gray-50 !border !border-gray-200 !rounded-lg my-1.5 ${isPdf ? 'pdf-section' : ''}`}
						header={
							<Flex justify="space-between" align="flex-start">
								<span>
									<p
										className={
											isPdf ? 'pdf-margin' : ''
										}>{`${category?.categoryName}: `}</p>
									<Text type="secondary" className={isPdf ? 'pdf-margin' : ''}>
										{category?.categoryDescription}
									</Text>
								</span>
								<Tag
									style={{ marginInlineEnd: 0 }}
									color={category?.categoryColor}>
									<p className={isPdf ? 'pdf-margin' : ''}>{category?.range}</p>
								</Tag>
							</Flex>
						}
						style={{
							borderLeft: `4px solid ${category.categoryColor}`,
							margin: '16px 0',
						}}>
						<Table
							bordered
							dataSource={category?.data}
							columns={columns}
							pagination={false}
							size="small"
						/>
					</Collapse.Panel>
				))}
			</Collapse>
			<Image
				style={{ display: 'none' }}
				src={processedImage || undefined}
				preview={{
					visible: modalOpen,
					onVisibleChange: visible => setModalOpen(visible),
				}}
			/>
		</div>
	);
};

export default ContentRomResult;
